import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MovementApproval } from '../../../models/MovementApproval';
import { MovementApprovalsService, MovementApprovalPagedResult } from '../../../services/movement-approvals.service';
import { PaginationOptions } from '../../../shared/interfaces/pagination.interface';
import { StockService } from '../../../services/stock.service';
import { WarehousesService } from '../../../services/warehouses.service';
import { StatusService } from '../../../services/status.service';
import { Status } from '../../../models/Status';
import { ErrorService } from '../../../shared/services/error.service';
import { StockMovementsService } from '../../../services/stock-movements.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { Stock } from '../../../models/Stock';
import { Warehouse } from '../../../models/Warehouse';

@Component({
  selector: 'app-movement-approvals',
  templateUrl: './movement-approvals.component.html',
  styleUrls: ['./movement-approvals.component.css']
})
export class MovementApprovalsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isSubmittingDecision = false;
  showDecisionModal = false;
  decisionAction: 'approve' | 'reject' | null = null;
  selectedMovementId: number | null = null;
  decisionJustification = '';
  showViewModal = false;
  isLoadingView = false;
  selectedApprovalDetail: MovementApproval | null = null;

  // ===== DADOS =====
  movementApprovals: MovementApproval[] = [];
  filteredData: MovementApproval[] = [];
  stocks: Stock[] = [];
  warehouses: Warehouse[] = [];
  isLoading = false;
  error: string | null = null;

  // ===== PAGINAÇÃO =====
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // ===== FILTROS =====
  filters = {
    status: '',
    stockId: '',
    warehouseId: ''
  };

  statuses: Status[] = [];

  // ===== CONFIGURAÇÃO DA TABELA =====
  tableTitle = 'Aprovações de Movimentação';
  showSearch = true;
  searchPlaceholder = 'Buscar por aprovador, justificativa...';
  clickable = true;

  columns = [
    { titulo: 'ID', label: 'id' },
    { titulo: 'Movimentação', label: 'stockMovementId' },
    { titulo: 'Quantidade', label: 'stockMovementQuantityLabel' },
    { titulo: 'Data Mov.', label: 'stockMovementDateLabel' },
    { titulo: 'Solicitante', label: 'solicitanteNomeLabel' },
    { titulo: 'Aprovador', label: 'aprovadorNomeLabel' },
    { titulo: 'Status', label: 'statusLabel' },
    { titulo: 'Justificativa', label: 'justification' }
  ];

  actions: TableAction[] = [
    {
      label: 'Aprovar',
      icon: 'fas fa-circle-check',
      action: 'approve',
      class: 'btn-success'
    },
    {
      label: 'Rejeitar',
      icon: 'fas fa-ban',
      action: 'reject',
      class: 'btn-delete'
    },
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    }
  ];

  constructor(
    private movementApprovalsService: MovementApprovalsService,
    private stockMovementsService: StockMovementsService,
    private stockService: StockService,
    private warehousesService: WarehousesService,
    private errorService: ErrorService,
    private statusService: StatusService
  ) {}

  ngOnInit(): void {
    this.loadStatuses();
    this.loadStocks();
    this.loadWarehouses();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStatuses(): void {
    this.statusService.getByEntity('MovementApproval').subscribe({
      next: (statuses) => {
        this.statuses = statuses;
      },
      error: (err) => {
        console.error('Erro ao carregar status', err);
        this.statuses = [];
      }
    });
  }

  loadStocks(): void {
    this.stockService.getAll({ page: 1, pageSize: 1000 }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.stocks = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar estoques:', error);
      }
    });
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.warehouses = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar almoxarifados:', error);
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    this.error = null;
    const options: PaginationOptions = { page: this.currentPage, pageSize: this.pageSize };

    this.movementApprovalsService.getAll(options).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: MovementApprovalPagedResult) => {
        this.movementApprovals = (result.items || []).map((x: MovementApproval) => ({
          ...x,
          stockMovementQuantityLabel: this.formatNumber(x.stockMovementQuantity),
          stockMovementDateLabel: this.formatDateTimeLabel(x.stockMovementDate),
          approvalDateLabel: this.formatDateTimeLabel(x.approvalDate),
          solicitanteNomeLabel: x.solicitanteNome || x.createdBy || '—',
          aprovadorNomeLabel: x.aprovadorNome || x.approverName || '—',
          approvedByLabel: x.approvedBy || x.updatedBy || '—',
          statusLabel: this.translateStatus(x.status)
        })) as MovementApproval[];
        this.totalItems = result.totalItems;
        this.totalPages = result.totalPages;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.currentPage = result.page;
        this.pageSize = result.pageSize;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar aprovações:', error);
        this.error = 'Erro ao carregar aprovações. Tente novamente.';
        this.isLoading = false;
        this.movementApprovals = [];
        this.filteredData = [];
      }
    });
  }

  applyFilters(): void {
    let filtered = [...this.movementApprovals];

    if (this.filters.status) {
      filtered = filtered.filter(approval => approval.status === this.filters.status);
    }

    // Filtro de estoque - por enquanto não implementado pois MovementApproval não tem referência direta ao estoque
    // if (this.filters.stockId) {
    //   const stockId = Number(this.filters.stockId);
    //   filtered = filtered.filter(approval => {
    //     // Assumindo que a aprovação tem uma referência ao estoque através da movimentação
    //     // Isso pode precisar ser ajustado dependendo do modelo de dados
    //     return true; // Temporário - precisa verificar como o estoque está relacionado
    //   });
    // }

    if (this.filters.warehouseId) {
      const warehouseId = Number(this.filters.warehouseId);
      filtered = filtered.filter(approval => {
        // Assumindo que a aprovação tem uma referência ao almoxarifado através da movimentação
        // Isso pode precisar ser ajustado dependendo do modelo de dados
        return true; // Temporário - precisa verificar como o almoxarifado está relacionado
      });
    }

    this.filteredData = filtered;
  }

  clearFilters(): void {
    this.filters = {
      status: '',
      stockId: '',
      warehouseId: ''
    };
    this.applyFilters();
  }

  onActionClick(event: { action: string, item: any }): void {
    const { action, item } = event;

    switch (action) {
      case 'approve':
        this.approveMovement(item);
        break;
      case 'reject':
        this.rejectMovement(item);
        break;
      case 'view':
        this.viewApproval(item);
        break;
    }
  }

  approveMovement(approval: MovementApproval): void {
    const movementId = Number(approval?.stockMovementId || 0);
    if (!movementId) {
      this.errorService.showErrorMessage('Não foi possível identificar a movimentação para aprovar.');
      return;
    }
    this.openDecisionModal('approve', movementId);
  }

  rejectMovement(approval: MovementApproval): void {
    const movementId = Number(approval?.stockMovementId || 0);
    if (!movementId) {
      this.errorService.showErrorMessage('Não foi possível identificar a movimentação para reprovar.');
      return;
    }
    this.openDecisionModal('reject', movementId);
  }

  openDecisionModal(action: 'approve' | 'reject', movementId: number): void {
    this.decisionAction = action;
    this.selectedMovementId = movementId;
    this.decisionJustification = '';
    this.showDecisionModal = true;
  }

  closeDecisionModal(): void {
    if (this.isSubmittingDecision) {
      return;
    }
    this.showDecisionModal = false;
    this.decisionAction = null;
    this.selectedMovementId = null;
    this.decisionJustification = '';
  }

  confirmDecision(): void {
    const movementId = Number(this.selectedMovementId || 0);
    const justification = this.decisionJustification.trim();
    if (!movementId || !this.decisionAction) {
      this.errorService.showErrorMessage('Dados inválidos para decisão da aprovação.');
      return;
    }
    if (!justification) {
      this.errorService.showErrorMessage('A justificativa é obrigatória.');
      return;
    }

    this.isSubmittingDecision = true;
    const request$ = this.decisionAction === 'approve'
      ? this.stockMovementsService.approveMovement(movementId, { justification })
      : this.stockMovementsService.rejectMovement(movementId, { justification });

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage(
            this.decisionAction === 'approve'
              ? 'Movimentação aprovada com sucesso.'
              : 'Movimentação reprovada com sucesso.'
          );
          this.isSubmittingDecision = false;
          this.closeDecisionModal();
          this.loadData();
        },
        error: () => {
          this.errorService.showErrorMessage(
            this.decisionAction === 'approve'
              ? 'Não foi possível aprovar a movimentação.'
              : 'Não foi possível reprovar a movimentação.'
          );
          this.isSubmittingDecision = false;
        }
      });
  }

  viewApproval(approval: MovementApproval): void {
    const approvalId = Number(approval?.id || 0);
    if (!approvalId) {
      this.errorService.showErrorMessage('Não foi possível identificar a aprovação para visualizar.');
      return;
    }

    this.isLoadingView = true;
    this.showViewModal = true;
    this.selectedApprovalDetail = null;
    this.movementApprovalsService.getById(approvalId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.selectedApprovalDetail = detail;
          this.isLoadingView = false;
        },
        error: () => {
          this.isLoadingView = false;
          this.errorService.showErrorMessage('Não foi possível carregar os detalhes da aprovação.');
          this.closeViewModal();
        }
      });
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.isLoadingView = false;
    this.selectedApprovalDetail = null;
  }

  formatDateTime(value?: string): string {
    if (!value) return '—';
    if (value.startsWith('0001-01-01')) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1) return '—';
    return d.toLocaleString('pt-BR');
  }

  retryLoad(): void {
    this.loadData();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadData();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadData();
  }

  // ===== HELPERS =====
  private formatDateTimeLabel(value?: string): string {
    if (!value) return '—';
    if (value.startsWith('0001-01-01')) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1) return '—';
    return d.toLocaleString('pt-BR');
  }

  private formatNumber(value?: number): string {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return '—';
    }
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  }

  private translateStatus(status?: string): string {
    const map: { [k: string]: string } = {
      Approved: 'Aprovado',
      Rejected: 'Reprovado',
      Pending: 'Pendente',
      Aprovado: 'Aprovado',
      Reprovado: 'Reprovado'
    };
    return map[status || ''] || status || '—';
  }

  getApprovalStatusLabel(status?: string): string {
    return this.translateStatus(status);
  }

  getPendingCount(): number {
    return this.movementApprovals.filter(approval => {
      const s = (approval.status || '').toLowerCase();
      return s === 'pending' || s === 'pendente';
    }).length;
  }

  getApprovedCount(): number {
    return this.movementApprovals.filter(approval => {
      const s = (approval.status || '').toLowerCase();
      return s === 'approved' || s === 'aprovado';
    }).length;
  }

  getRejectedCount(): number {
    return this.movementApprovals.filter(approval => {
      const s = (approval.status || '').toLowerCase();
      return s === 'rejected' || s === 'reprovado';
    }).length;
  }
}
